import React, { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import AgentAvatar from "../AgentAvatar";
import { AVATAR_SIZE, TEXT_INDENT } from "./conversationConstants";
import {
  FormSurface,
  StatusPill,
  useFormPalette,
} from "../forms/DynamicFormPrimitives";
import { parseMaybeJson } from "./conversationUtils";
import CredentialSetupModal from "./CredentialSetupModal";

const OUTCOME_CREATED = "created";
const OUTCOME_SKIPPED = "skipped";

/** "demo_api_key" -> "Demo Api Key". Fallback display name when the agent didn't supply one. */
function humanizePlaceholder(key) {
  return String(key || "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const OUTCOME_LABEL = {
  created: "Secret added",
  use_existing: "Linked existing secret",
  skipped: "Skipped",
};

/**
 * Inline card for the agent's `request_credential_setup` tool, modeled on AskHumanCard. The PENDING
 * state offers "Add secret" (opens CredentialSetupModal, locked to the agent's requested
 * placeholder) and "Skip". On completion the parent's onSubmitCredentialSetup resumes the agent
 * loop; the resumed session streams a COMPLETED envelope back onto this part, flipping it to the
 * read-only answered state. This is a self-contained island parallel to the ask_human flow.
 */
export default function CredentialSetupCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  chatSessionId,
  onSubmitCredentialSetup = null,
  onOpenAgentSettings = null,
}) {
  const palette = useFormPalette();
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);

  const setupId = parsedResult?.setupId || parsedArgs?.setupId || null;
  const placeholder =
    parsedResult?.placeholder || parsedArgs?.placeholder || "";
  const secretToken =
    parsedResult?.secretToken ||
    (placeholder ? `\${${placeholder}.SECRET}` : "");
  const reason = parsedArgs?.reason || parsedResult?.reason || "";
  // Display Name / Description are pre-filled by the agent (request_credential_setup now requires
  // displayName + description). Fall back to a humanized placeholder and the reason so the modal is
  // never empty even for older tool calls that predate those params.
  const suggestedDisplayName =
    parsedArgs?.displayName ||
    parsedResult?.displayName ||
    humanizePlaceholder(placeholder);
  const suggestedDescription =
    parsedArgs?.description || parsedResult?.description || reason || "";

  const isCompleted = parsedResult?.status === "COMPLETED";
  const outcome = parsedResult?.outcome || null;

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const canSubmit =
    typeof onSubmitCredentialSetup === "function" && Boolean(setupId);
  const interactive = !isCompleted && !submitting;

  const avatarClick =
    agentId && typeof onOpenAgentSettings === "function"
      ? (e) => {
          e.stopPropagation();
          onOpenAgentSettings(agentId);
        }
      : undefined;

  const submitCompletion = async (payload) => {
    if (!canSubmit || submitting) return false;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ok = await onSubmitCredentialSetup(chatSessionId, {
        setupId,
        ...payload,
      });
      if (ok === false) {
        setSubmitError("Couldn't complete setup. Please try again.");
        setSubmitting(false);
        return false;
      }
      // On success the resumed session streams the COMPLETED envelope onto this part, which flips
      // the card to its read-only state, so we intentionally leave controls locked.
      return true;
    } catch (e) {
      setSubmitError("Couldn't complete setup. Please try again.");
      setSubmitting(false);
      return false;
    }
  };

  const handleSaved = async ({ credentialId, placeholder: savedPlaceholder }) => {
    const ok = await submitCompletion({
      outcome: OUTCOME_CREATED,
      credentialId,
      placeholder: savedPlaceholder || placeholder,
    });
    if (ok) setModalOpen(false);
  };

  const handleSkip = () => {
    submitCompletion({ outcome: OUTCOME_SKIPPED });
  };

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar
            agentId={agentId}
            name={agentName}
            size={AVATAR_SIZE}
            onClick={avatarClick}
          />
        )}
        <Box sx={{ width: "75%", minWidth: "280px" }}>
          <FormSurface
            title="Set up credential"
            rightSlot={
              isCompleted ? (
                <StatusPill
                  label={OUTCOME_LABEL[outcome] || "Done"}
                  tone={outcome === OUTCOME_SKIPPED ? "neutral" : "success"}
                />
              ) : undefined
            }
          >
            {reason && (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: palette.textPrimary,
                  mb: 1.25,
                }}
              >
                {reason}
              </Typography>
            )}

            {secretToken && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  mb: interactive ? 1.25 : 0,
                  borderRadius: "8px",
                  border: `1px solid ${palette.chipBorder}`,
                  backgroundColor: palette.selectedBg,
                }}
              >
                <KeyOutlinedIcon
                  sx={{ fontSize: 15, color: palette.textSecondary }}
                />
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "12.5px",
                    color: palette.textPrimary,
                  }}
                >
                  {secretToken}
                </Typography>
              </Box>
            )}

            {!isCompleted && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.25,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disabled={submitting || !canSubmit}
                  onClick={() => setModalOpen(true)}
                  startIcon={<KeyOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: "none", fontSize: "12px" }}
                >
                  Add secret
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={submitting || !canSubmit}
                  onClick={handleSkip}
                  sx={{ textTransform: "none", fontSize: "12px" }}
                >
                  Skip
                </Button>
                {submitting && (
                  <CircularProgress size={14} sx={{ color: palette.accent }} />
                )}
              </Box>
            )}

            {submitError && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: palette.errorText,
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {submitError}
              </Typography>
            )}
          </FormSurface>
        </Box>
      </Box>

      {modalOpen && (
        <CredentialSetupModal
          open={modalOpen}
          initialPlaceholder={placeholder}
          suggestedDisplayName={suggestedDisplayName}
          suggestedDescription={suggestedDescription}
          lockPlaceholder={true}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </Box>
  );
}
